import { supabase } from '@/integrations/supabase/client';

/**
 * Script para migrar leads do Facebook que não têm form_submission_id
 * Cria registros de form_submission retroativamente usando os dados do raw_data
 */

async function migrateFacebookLeadsToFormSubmissions() {
  console.log('🚀 Iniciando migração de leads do Facebook para form_submissions...');

  try {
    // Busca todos os leads do Facebook sem form_submission_id
    const { data: leadsWithoutSubmission, error: fetchError } = await supabase
      .from('leads')
      .select('*')
      .eq('source_platform', 'facebook')
      .is('form_submission_id', null)
      .order('created_at', { ascending: true });

    if (fetchError) {
      console.error('❌ Erro ao buscar leads:', fetchError);
      return;
    }

    if (!leadsWithoutSubmission || leadsWithoutSubmission.length === 0) {
      console.log('✅ Nenhum lead sem form_submission encontrado!');
      return;
    }

    console.log(`📊 Encontrados ${leadsWithoutSubmission.length} leads sem form_submission`);

    let successCount = 0;
    let errorCount = 0;

    // Processa cada lead
    for (const lead of leadsWithoutSubmission) {
      try {
        console.log(`\n📝 Processando lead: ${lead.first_name} ${lead.last_name} (ID: ${lead.id})`);

        // Extrai dados do raw_data se disponível
        const rawData = lead.raw_data || {};
        const fieldData = rawData.fieldData || {};

        // Busca a campanha se disponível
        let campaignName = 'Facebook Lead Form';
        if (lead.campaign_id) {
          const { data: campaign } = await supabase
            .from('ad_campaigns')
            .select('campaign_name')
            .eq('id', lead.campaign_id)
            .maybeSingle();
          
          if (campaign) {
            campaignName = campaign.campaign_name;
          }
        }

        // Cria o form_submission
        const { data: formSubmission, error: formError } = await supabase
          .from('form_submissions')
          .insert({
            form_type: 'facebook_lead_ad',
            form_name: campaignName,
            first_name: lead.first_name,
            last_name: lead.last_name,
            email: lead.primary_email,
            phone: lead.primary_phone,
            market: lead.market,
            created_at: lead.created_at, // Mantém a data original
            form_data: {
              ...fieldData,
              leadgen_id: lead.facebook_lead_id,
              form_id: lead.facebook_form_id,
              page_id: lead.facebook_page_id,
              ad_id: rawData.ad_id,
              adset_id: rawData.adset_id,
              campaign_id: rawData.campaign_id,
              created_time: rawData.created_time,
              raw_facebook_data: rawData,
              migrated: true,
              migrated_at: new Date().toISOString()
            }
          })
          .select()
          .single();

        if (formError) {
          console.error(`  ❌ Erro ao criar form_submission:`, formError.message);
          errorCount++;
          continue;
        }

        console.log(`  ✅ Form submission criado (ID: ${formSubmission.id})`);

        // Atualiza o lead com o form_submission_id
        const { error: updateError } = await supabase
          .from('leads')
          .update({ form_submission_id: formSubmission.id })
          .eq('id', lead.id);

        if (updateError) {
          console.error(`  ❌ Erro ao atualizar lead:`, updateError.message);
          errorCount++;
          continue;
        }

        console.log(`  ✅ Lead atualizado com form_submission_id`);
        successCount++;

      } catch (error) {
        console.error(`  ❌ Erro ao processar lead ${lead.id}:`, error);
        errorCount++;
      }
    }

    console.log('\n' + '='.repeat(60));
    console.log('📊 RESUMO DA MIGRAÇÃO:');
    console.log(`  ✅ Sucesso: ${successCount} leads`);
    console.log(`  ❌ Erros: ${errorCount} leads`);
    console.log(`  📝 Total processado: ${leadsWithoutSubmission.length} leads`);
    console.log('='.repeat(60));

    if (successCount > 0) {
      console.log('\n🎉 Migração concluída com sucesso!');
    }

  } catch (error) {
    console.error('❌ Erro geral na migração:', error);
  }
}

// Função para reverter a migração (útil para testes)
async function revertMigration() {
  console.log('⚠️  Revertendo migração...');

  try {
    // Busca form_submissions que foram migrados
    const { data: migratedSubmissions, error: fetchError } = await supabase
      .from('form_submissions')
      .select('id')
      .eq('form_type', 'facebook_lead_ad')
      .contains('form_data', { migrated: true });

    if (fetchError) {
      console.error('❌ Erro ao buscar form_submissions:', fetchError);
      return;
    }

    if (!migratedSubmissions || migratedSubmissions.length === 0) {
      console.log('✅ Nenhum form_submission migrado encontrado');
      return;
    }

    const submissionIds = migratedSubmissions.map(s => s.id);

    // Remove form_submission_id dos leads
    const { error: updateError } = await supabase
      .from('leads')
      .update({ form_submission_id: null })
      .in('form_submission_id', submissionIds);

    if (updateError) {
      console.error('❌ Erro ao atualizar leads:', updateError);
      return;
    }

    // Deleta os form_submissions migrados
    const { error: deleteError } = await supabase
      .from('form_submissions')
      .delete()
      .in('id', submissionIds);

    if (deleteError) {
      console.error('❌ Erro ao deletar form_submissions:', deleteError);
      return;
    }

    console.log(`✅ Migração revertida: ${migratedSubmissions.length} registros removidos`);

  } catch (error) {
    console.error('❌ Erro ao reverter migração:', error);
  }
}

/**
 * Script para migrar leads de eligibility_form que não têm form_submission_id
 */
async function migrateEligibilityLeadsToFormSubmissions() {
  console.log('🚀 Iniciando migração de leads de eligibility_form para form_submissions...');

  try {
    // Busca todos os leads de eligibility_form sem form_submission_id
    const { data: leadsWithoutSubmission, error: fetchError } = await supabase
      .from('leads')
      .select('*')
      .eq('source_platform', 'eligibility_form')
      .is('form_submission_id', null)
      .order('created_at', { ascending: true });

    if (fetchError) {
      console.error('❌ Erro ao buscar leads:', fetchError);
      return;
    }

    if (!leadsWithoutSubmission || leadsWithoutSubmission.length === 0) {
      console.log('✅ Nenhum lead de eligibility_form sem form_submission encontrado!');
      return;
    }

    console.log(`📊 Encontrados ${leadsWithoutSubmission.length} leads de eligibility_form sem form_submission`);

    let successCount = 0;
    let errorCount = 0;

    // Processa cada lead
    for (const lead of leadsWithoutSubmission) {
      try {
        console.log(`\n📝 Processando lead: ${lead.first_name} ${lead.last_name} (ID: ${lead.id})`);

        // Cria o form_submission
        const { data: formSubmission, error: formError } = await supabase
          .from('form_submissions')
          .insert({
            form_type: 'eligibility_form',
            form_name: 'Formulário de Elegibilidade',
            first_name: lead.first_name,
            last_name: lead.last_name,
            email: lead.primary_email,
            phone: lead.primary_phone,
            market: lead.market,
            created_at: lead.created_at, // Mantém a data original
            form_data: {
              eligibility_assessment: true,
              migrated: true,
              migrated_at: new Date().toISOString(),
              original_lead_id: lead.id
            }
          })
          .select()
          .single();

        if (formError) {
          console.error(`  ❌ Erro ao criar form_submission:`, formError.message);
          errorCount++;
          continue;
        }

        console.log(`  ✅ Form submission criado (ID: ${formSubmission.id})`);

        // Atualiza o lead com o form_submission_id
        const { error: updateError } = await supabase
          .from('leads')
          .update({ form_submission_id: formSubmission.id })
          .eq('id', lead.id);

        if (updateError) {
          console.error(`  ❌ Erro ao atualizar lead:`, updateError.message);
          errorCount++;
          continue;
        }

        console.log(`  ✅ Lead atualizado com form_submission_id`);
        successCount++;

      } catch (error) {
        console.error(`  ❌ Erro ao processar lead ${lead.id}:`, error);
        errorCount++;
      }
    }

    console.log('\n' + '='.repeat(60));
    console.log('📊 RESUMO DA MIGRAÇÃO:');
    console.log(`  ✅ Sucesso: ${successCount} leads`);
    console.log(`  ❌ Erros: ${errorCount} leads`);
    console.log(`  📝 Total processado: ${leadsWithoutSubmission.length} leads`);
    console.log('='.repeat(60));

    if (successCount > 0) {
      console.log('\n🎉 Migração concluída com sucesso!');
    }

  } catch (error) {
    console.error('❌ Erro geral na migração:', error);
  }
}

// Exporta as funções
export { migrateFacebookLeadsToFormSubmissions, migrateEligibilityLeadsToFormSubmissions, revertMigration };

// Para executar diretamente no console do navegador:
// import { migrateFacebookLeadsToFormSubmissions, migrateEligibilityLeadsToFormSubmissions } from './scripts/migrateFacebookLeadsToFormSubmissions.js';
// migrateFacebookLeadsToFormSubmissions(); // Para leads do Facebook
// migrateEligibilityLeadsToFormSubmissions(); // Para leads de eligibility_form
